import React, { useEffect, useMemo, useState } from "react"
import { useUser } from "util/UserContext"
import { Info, GripVertical, X } from "lucide-react"
import Modal from "ui/Modal"
import RankedDragList from "ui/RankedDragList"
import {
  getIssueRankings,
  addIssueRanking,
  removeIssueRanking,
  reorderIssueRankings,
  getNextIssueRankingPosition,
  IssueRanking,
} from "util/userUtils"
import IssueSearch, { Suggestion } from "components/Issues/IssueSearch"
import api from "config/axiosConfig"
import { Issue } from "util/constants"
import { toast } from "sonner"
import EntityAvatar from "components/Entities/EntityAvatar"
import { getEntityDetails } from "util/getObjectDetails"
import EntitySubscription from "ui/EntitySubscription"
import EntitySentiment from "components/Alignment/EntitySentiment"
import { useIssueFromSuggestion } from "hooks/useIssueFromSuggestion"
import EmptyState from "ui/EmptyState"
import { apiRoutes } from "util/apiRoutes";

interface Props {
  onClose: () => void
  maxIssues?: number
  title?: string
  description?: string
  hideAutoSubscribe?: boolean
  onRankingsChange?: (rankings: IssueRanking[]) => void
  actionLabel?: string
  renderInline?: boolean
  copy?: Partial<{
    searchPlaceholder: string
    subscribeLabel: string
    subscribeDescription: string
    loadError: string
    invalidIssue: string
    duplicateIssue: string
    addSuccess: string
    addError: string
    subscribeSuccess: string
    subscribeError: string
    removeSuccess: string
    removeError: string
    reorderSuccess: string
    reorderError: string
    emptyTitle: string
    emptySubtitle: string
    removeIssueLabel: string
    maxIssuesReached: string
  }>
}

// Presentational component: accepts handleProps for drag handle
const RankingItem: React.FC<{
  ranking: IssueRanking
  removeLabel: string
  showRemove?: boolean
  onRemove?: () => void
  isDragOverlay?: boolean
  handleProps?: React.HTMLAttributes<HTMLElement>
}> = ({
  ranking,
  removeLabel,
  showRemove = true,
  onRemove,
  isDragOverlay = false,
  handleProps
}) => {

  const issue = ranking.issue
  const issueId = issue?.id

  return (
    <div
      className={`group relative flex items-center gap-2 p-1 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-xl border border-eoe-border-main dark:border-eoe-dark-border-main transition-all duration-200 ${
        isDragOverlay ? 'shadow-xl scale-105' : 'hover:shadow-md hover:border-eoe-gray dark:hover:border-eoe-gray'
      }`}
    >
      {/* Drag Handle */}
      <div
        {...handleProps}
        className="flex-shrink-0 flex items-center gap-1 p-1 cursor-grab select-none"
        style={{ touchAction: 'none' }}
      >
        <GripVertical className="size-4" />
        <div className="w-6 h-6 rounded-full bg-eoe-gray text-white dark:bg-eoe-gray text-xs font-semibold flex items-center justify-center">
          {ranking.ranking}
        </div>
      </div>

      {/* Content with sentiment below avatar */}
      <div className="flex-grow min-w-0 flex flex-col">
        <EntityAvatar
          entity={issue as Issue | undefined}
          entityType="issue"
          className="flex-grow min-w-0"
          entityChip={true}
          showCloseChip={false}
          badgeElement={issueId ? <EntitySubscription entityId={issueId} entityType="issue" variant="mini" showLabel={false} /> : undefined}
        />
        {/* Sentiment component placed below the avatar (hidden in drag overlay) */}
        {!isDragOverlay && issueId ? (
          <EntitySentiment entityType="issue" entityId={issueId} className="mt-1 flex-shrink-0 px-1" variant="bar" />
        ) : null}
      </div>

      {/* Remove Button */}
      {showRemove && onRemove && (
        <button
          onClick={onRemove}
          className="flex-shrink-0 p-1 rounded-full text-warning-red hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover transition-colors"
          aria-label={removeLabel}
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  )
}

const DEFAULT_COPY = {
  searchPlaceholder: "Search for an issue to add",
  subscribeLabel: "Subscribe",
  subscribeDescription: "Automatically subscribe to notifications to stay updated on issues you add.",
  loadError: "Failed to load existing issue rankings.",
  invalidIssue: "Invalid issue selected.",
  duplicateIssue: "This issue is already in your rankings.",
  addSuccess: "Issue added to rankings!",
  addError: "Failed to add issue to rankings.",
  subscribeSuccess: "Subscribed to this issue",
  subscribeError: "Failed to subscribe to issue",
  removeSuccess: "Issue removed from rankings!",
  removeError: "Failed to remove issue ranking.",
  reorderSuccess: "Rankings updated!",
  reorderError: "Failed to update ranking order.",
  emptyTitle: "No issues ranked yet",
  emptySubtitle: "Search and add issues below to get started.",
  removeIssueLabel: "Remove issue",
  maxIssuesReached: "You have already selected the maximum number of issues.",
} as const

const UserAboutIssueModal: React.FC<Props> = ({
  onClose,
  maxIssues,
  title = "Prioritea",
  description = "Rank your top issues in order of importance. Add issues and drag to reorder. Optionally, specify your sentiment towards the issue.",
  hideAutoSubscribe = false,
  onRankingsChange,
  actionLabel,
  renderInline = false,
  copy = {},
}) => {
  const { refreshUser } = useUser()
  const [issueRankings, setIssueRankings] = useState<IssueRanking[]>([])
  const [selectedIssue, setSelectedIssue] = useState<Suggestion | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [autoSubscribe, setAutoSubscribe] = useState<boolean>(true)
  const resolvedCopy = useMemo(() => ({ ...DEFAULT_COPY, ...copy }), [copy])
  const displayedRankings = useMemo(
    () => (typeof maxIssues === "number" ? issueRankings.slice(0, maxIssues) : issueRankings),
    [issueRankings, maxIssues]
  )

  // Load existing rankings
  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true)
        const { data } = await getIssueRankings()
        const enriched = await Promise.all(
          (data || []).map(async (r: IssueRanking) => {
            try {
              const full = await getEntityDetails("issue", r.issueId)
              return {
                ...r,
                issue: {
                  id: full.id,
                  name: full.name ?? "",
                  cname: full.cname ?? "",
                  properties: full.properties,
                },
              }
            } catch {
              return r
            }
          })
        )
        enriched.sort((a, b) => a.ranking - b.ranking)
        setIssueRankings(enriched)
      } catch {
        setError(resolvedCopy.loadError)
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [resolvedCopy.loadError])

  useEffect(() => {
    onRankingsChange?.(displayedRankings)
  }, [displayedRankings, onRankingsChange])

  const { ensureIssue } = useIssueFromSuggestion()

  // Add new issue (optionally from passed suggestion for immediate add-on-select)
  const handleAddIssue = async (issueToAdd?: Suggestion | null) => {
    const chosen = issueToAdd ?? selectedIssue
    if (!chosen) return
    if (typeof maxIssues === "number" && displayedRankings.length >= maxIssues) {
      setError(resolvedCopy.maxIssuesReached)
      return
    }
    setIsSubmitting(true)
    setError(null)
    try {
      // Resolve to a concrete Issue using idempotent helper
      const resolved = chosen.type === 'issue'
        ? { id: (typeof (chosen as { id?: number }).id === 'number' ? (chosen as { id: number }).id : null) }
        : await ensureIssue(chosen)

      const issueId = typeof (resolved as { id?: number }).id === 'number'
        ? (resolved as { id: number }).id
        : null

      if (!issueId) {
        setError(resolvedCopy.invalidIssue)
        return
      }

      if (issueRankings.some(r => r.issueId === issueId)) {
        setError(resolvedCopy.duplicateIssue)
        return
      }

      const nextPos = await getNextIssueRankingPosition()
      const addRes = await addIssueRanking(issueId, nextPos, 'public')
      const full = await getEntityDetails("issue", issueId)

      const newR: IssueRanking = {
        id: addRes.id,
        userId: addRes.user_id,
        issueId,
        ranking: nextPos,
        privacy: "public",
        createdAt: addRes.created_at,
        updatedAt: addRes.updated_at,
        issue: {
          id: full.id,
          name: full.name ?? "",
          cname: full.cname ?? "",
          properties: full.properties,
        },
      }

      setIssueRankings(prev => [...prev, newR].sort((a, b) => a.ranking - b.ranking))
      setSelectedIssue(null)
      setHasChanges(true)
      toast.success(resolvedCopy.addSuccess)
      // Auto-subscribe to the issue if toggle is enabled
      if (autoSubscribe && !hideAutoSubscribe) {
        try {
          await api.post(apiRoutes.user.subscriptions, {
            entityType: 'issue',
            entityId: issueId,
            level: 1,
            privacy: 'public'
          })
          toast.success(resolvedCopy.subscribeSuccess)
        } catch (subscribeErr) {
          console.error('Failed to subscribe to issue:', subscribeErr)
          toast.error(resolvedCopy.subscribeError)
        }
      }
    } catch {
      setError(resolvedCopy.addError)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Remove ranking
  const handleRemoveRanking = async (r: IssueRanking) => {
    try {
      await removeIssueRanking(r.id)
      const filtered = issueRankings.filter(x => x.id !== r.id)
      const renumbered = filtered.map(x =>
        x.ranking > r.ranking ? { ...x, ranking: x.ranking - 1 } : x
      )
      renumbered.sort((a, b) => a.ranking - b.ranking)
      setIssueRankings(renumbered)
      // update server positions
      const toUpdate = renumbered
        .filter(x => x.ranking >= r.ranking)
        .map(x => ({ relationId: x.id, ranking: x.ranking }))
      if (toUpdate.length) await reorderIssueRankings(toUpdate)
      setHasChanges(true)
      toast.success(resolvedCopy.removeSuccess)
    } catch {
      toast.error(resolvedCopy.removeError)
    }
  }

  const handleReorderRankings = async (nextOrder: IssueRanking[]) => {
    const visibleRelationIds = new Set(nextOrder.map((item) => item.id))
    const hiddenRankings = issueRankings
      .filter((item) => !visibleRelationIds.has(item.id))
      .sort((a, b) => a.ranking - b.ranking)
    const reindexedVisible = nextOrder.map((item, index) => ({
      ...item,
      ranking: index + 1
    }))
    const reindexedHidden = hiddenRankings.map((item, index) => ({
      ...item,
      ranking: reindexedVisible.length + index + 1
    }))
    const reindexed = [...reindexedVisible, ...reindexedHidden]
    const previous = issueRankings
    setIssueRankings(reindexed)
    setHasChanges(true)
    try {
      await reorderIssueRankings(reindexed.map(x => ({ relationId: x.id, ranking: x.ranking })))
      toast.success(resolvedCopy.reorderSuccess)
    } catch {
      toast.error(resolvedCopy.reorderError)
      setIssueRankings(previous)
    }
  }

  const handleClose = async () => {
    if (hasChanges) await refreshUser()
    onClose()
  }

  const handleIssueSelect = (s: Suggestion) => {
    if (isSubmitting) return
    setError(null)
    setSelectedIssue(s)
    // Immediately add the selected issue to rankings
    void handleAddIssue(s)
  }
  const handleEntityChipClose = () => setSelectedIssue(null)

  const rankingList = (
    <div className="flex flex-col flex-1 flex-grow overflow-y-auto space-y-3">
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div
              key={i}
              className="animate-pulse bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover h-16 rounded-xl"
            />
          ))}
        </div>
      ) : displayedRankings.length > 0 ? (
        <RankedDragList
          items={displayedRankings}
          getId={(item) => item.id.toString()}
          onReorder={handleReorderRankings}
          renderItem={(ranking, handleProps) => (
            <RankingItem
              ranking={ranking}
              removeLabel={resolvedCopy.removeIssueLabel}
              showRemove={true}
              onRemove={() => handleRemoveRanking(ranking)}
              handleProps={handleProps}
            />
          )}
          renderOverlay={(ranking) => (
            <RankingItem ranking={ranking} removeLabel={resolvedCopy.removeIssueLabel} showRemove={false} isDragOverlay />
          )}
        />
      ) : (
        <EmptyState
          title={resolvedCopy.emptyTitle}
          subtitle={resolvedCopy.emptySubtitle}
          className="m-4"
        />
      )}
    </div>
  )

  const controls = (
    <div className="flex flex-col space-y-2 sm:space-y-0 sm:gap-x-2 py-3">
      <div className="space-y-2">
        {typeof maxIssues !== "number" || displayedRankings.length < maxIssues ? (
          <IssueSearch
            key={`issue-search-${issueRankings.length}`}
            onSelect={handleIssueSelect}
            placeholder={resolvedCopy.searchPlaceholder}
            inputType="add"
            fieldRequired={false}
            excludeIssueIds={issueRankings.map(r => r.issueId)}
            onEntityChipClose={handleEntityChipClose}
          />
        ) : null}
        {!hideAutoSubscribe ? (
          <div className="group flex justify-between items-center bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-md">
            <div className="flex flex-col items-start">
              <span className="text-eoe-text dark:text-eoe-dark-text text-sm">{resolvedCopy.subscribeLabel}</span>
              <p className="text-xs text-eoe-text dark:text-eoe-dark-text-sub">
                {resolvedCopy.subscribeDescription}
              </p>
            </div>
            <input
              type="checkbox"
              className="toggle-switch-input col-span-1 flex-shrink-0"
              checked={autoSubscribe}
              onChange={(e) => setAutoSubscribe(e.target.checked)}
            />
          </div>
        ) : null}
        {error && (
          <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
            <Info className="mr-1 size-4" />
            <span>{error}</span>
          </div>
        )}
      </div>
      <button
        onClick={handleClose}
        className="btn-secondary w-full sm:w-auto py-2 px-4"
      >
        {actionLabel || (hasChanges ? "Close" : "Cancel")}
      </button>
    </div>
  )

  const modalBody = (
    <div className="flex flex-col sm:h-auto overflow-x-hidden space-y-3">
      <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
        {description}
      </p>
      {rankingList}
    </div>
  )

  if (renderInline) {
    return (
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-4 py-8">
        <div className="space-y-2">
          <h1 className="text-3xl font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading">
            {title}
          </h1>
          <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {description}
          </p>
        </div>
        <section className="rounded-2xl border border-eoe-border-main bg-eoe-bg-modal p-5 dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-modal">
          {rankingList}
          <div className="mt-4 border-t border-eoe-border-main pt-4 dark:border-eoe-dark-border-main">
            {controls}
          </div>
        </section>
      </div>
    )
  }

  return (
    <Modal isOpen onClose={handleClose} title={title} footer={controls}>
      {modalBody}
    </Modal>
  )
}

export default UserAboutIssueModal
